import { CommandPaletteBody, CommandPaletteQuery } from 'src/components/command-palette';
import { Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { OrgTypeWithRole } from 'src/@types/org';
import { Role } from 'src/@types/role';
import { InviteStatus } from 'src/@types/userOrg';
import EmptyContent from 'src/components/empty-content';
import { orgSwitcherTabs, orgTabLabels } from './constants';
import OrgSwitcherTabs from './OrgSwitcherTabs';
import OrgSwitcherRow from './rows/OrgSwitcherRow';

export type OrgSwitcherContentProps = {
  orgs: OrgTypeWithRole[];
  onSelectOrg: (org: OrgTypeWithRole, role: Role | undefined) => void;
  onInviteStatusUpdated: () => void;
  onClose?: () => void;
  initialTab?: InviteStatus;
  listHeight?: number;
  visibleTabs?: InviteStatus[];
};

type SearchableOrg = {
  org: OrgTypeWithRole;
  tab: InviteStatus;
};

const createEmptyOrgCounts = (): Record<InviteStatus, number> => ({
  [InviteStatus.Accepted]: 0,
  [InviteStatus.Pending]: 0,
  [InviteStatus.Rejected]: 0,
});

const toSearchableOrg = (org: OrgTypeWithRole): SearchableOrg | null => {
  const deactivatedAt = org?.deactivated_at;
  const isActive = org?.is_active;

  if ((deactivatedAt !== null && deactivatedAt !== undefined) || isActive === false) {
    return null;
  }

  const inviteStatus = org?.invite_status;
  const tab = inviteStatus || InviteStatus.Accepted;

  return tab ? { org, tab } : null;
};

export default function OrgSwitcherContent({
  orgs,
  onSelectOrg,
  onInviteStatusUpdated,
  onClose,
  initialTab = InviteStatus.Accepted,
  listHeight = 400,
  visibleTabs = orgSwitcherTabs,
}: OrgSwitcherContentProps) {
  const availableTabs = visibleTabs.length ? visibleTabs : orgSwitcherTabs;
  const [searchText, setSearchText] = useState('');
  const [selectedTab, setSelectedTab] = useState<InviteStatus>(
    availableTabs.includes(initialTab) ? initialTab : availableTabs[0]
  );

  useEffect(() => {
    if (!availableTabs.includes(selectedTab)) {
      setSelectedTab(availableTabs.includes(initialTab) ? initialTab : availableTabs[0]);
    }
  }, [availableTabs, initialTab, selectedTab]);

  const searchableOrgs = useMemo(
    () =>
      orgs.reduce<SearchableOrg[]>((result, org) => {
        const searchableOrg = toSearchableOrg(org);

        if (searchableOrg) {
          result.push(searchableOrg);
        }

        return result;
      }, []),
    [orgs]
  );

  const orgCounts = useMemo(
    () =>
      searchableOrgs.reduce<Record<InviteStatus, number>>((counts, { tab }) => {
        counts[tab] += 1;

        return counts;
      }, createEmptyOrgCounts()),
    [searchableOrgs]
  );

  const filteredOrgs = useMemo(() => {
    const searchTextLower = searchText.trim().toLowerCase();

    return searchableOrgs.filter(({ org, tab }) => {
      const orgName = org?.name?.toLowerCase();

      return (
        availableTabs.includes(tab) &&
        tab === selectedTab &&
        (!searchTextLower || !orgName || orgName.includes(searchTextLower))
      );
    });
  }, [availableTabs, searchText, searchableOrgs, selectedTab]);

  return (
    <>
      <CommandPaletteQuery
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        onClose={onClose}
        placeholder="Search for an Organization..."
      />
      <OrgSwitcherTabs
        counts={orgCounts}
        selectedTab={selectedTab}
        onSelectedTabChange={setSelectedTab}
        tabs={availableTabs}
      />
      <CommandPaletteBody>
        <Stack sx={{ height: listHeight }}>
          {!filteredOrgs.length && (
            <EmptyContent
              title={
                searchText
                  ? 'No Organizations Found'
                  : `No ${orgTabLabels[selectedTab]} Organizations`
              }
              description={
                searchText ? `No organizations were found matching "${searchText}"` : undefined
              }
              height={140}
            />
          )}
          {filteredOrgs.length > 0 && (
            <List
              height={listHeight}
              itemCount={filteredOrgs?.length || 0}
              itemSize={75}
              width="100%"
              itemData={filteredOrgs}
            >
              {({ index, style }) => {
                const { org, tab } = filteredOrgs[index];

                return (
                  <Stack key={index} style={style}>
                    <OrgSwitcherRow
                      org={org}
                      tab={tab}
                      onSelectOrg={onSelectOrg}
                      onInviteStatusUpdated={onInviteStatusUpdated}
                      onClose={onClose}
                    />
                  </Stack>
                );
              }}
            </List>
          )}
        </Stack>
      </CommandPaletteBody>
    </>
  );
}
